import { Button, ButtonVariant, Spinner } from '@usealloy/component-library';
import { Collapse } from 'reactstrap';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';

import { ajaxFetch } from '~/helpers/api';
import Card from '~/entities/evaluations/diligence/Card/Card';
import { removeLeadingZero } from '~/helpers/formatting';
import { RiskLevels } from '~/constants';
import { routes } from '~/helpers/routes';
import { useFetchedUserContext } from '~/helpers/hooks';

import { useEvaluationFraudScores } from './hooks';
import log from '~/helpers/logging';


export const getDefaultRiskLevel = score => {
  let risk = RiskLevels.HIGH;
  if (score < 0.3) {
    risk = RiskLevels.LOW;
  } else if (score >= 0.3 && score < 0.6) {
    risk = RiskLevels.MEDIUM;
  }
  return risk;
};

export const getRiskLevel = (normScore, rawScore, serviceName, attribute, fraudData) => {
  // https://app.clubhouse.io/usealloy/story/12482/create-info-modal-for-add-l-service-score-data
  const result = fraudData && fraudData.filter(
    score => (score.service_name === serviceName && score.model === attribute),
  );
  // if there's a servicename+model match in the fraud score database
  if (result && !!result.length) {
    const lowScore = parseFloat(result[0].low);
    const highScore = parseFloat(result[0].high);
    // checks whether low < high for rating
    if (lowScore < highScore) {
      if (rawScore < lowScore) {
        return RiskLevels.LOW;
      } if (rawScore <= highScore) {
        return RiskLevels.MEDIUM;
      }
      return RiskLevels.HIGH;
    } // captures high >= low
    if (rawScore > lowScore) {
      return RiskLevels.LOW;
    } if (rawScore >= highScore) {
      return RiskLevels.MEDIUM;
    }
    return RiskLevels.HIGH;
  }
  // else default to normalized scoring system if present
  return getDefaultRiskLevel(normScore);
};

export const sortKeysByRisk = (sourceData = {}, fraudScoreData = []) => {
  let riskLevel = {
    [RiskLevels.HIGH]: [],
    [RiskLevels.MEDIUM]: [],
    [RiskLevels.LOW]: [],
  };
  Object.keys(sourceData).forEach(source => {
    const riskSetting = getRiskLevel(
      sourceData[source].normalized_score,
      sourceData[source].raw_score,
      source,
      sourceData[source].attribute,
      fraudScoreData,
    );
    riskLevel[riskSetting].push({ name: source, ...sourceData[source], riskLevel: riskSetting });
  });
  riskLevel = [RiskLevels.HIGH, RiskLevels.MEDIUM, RiskLevels.LOW].map(
    risk => riskLevel[risk].sort((a, b) => (a.name > b.name ? 1 : -1)),
  );
  return [].concat(...riskLevel);
};

const sortFraudScoresByRisk = (fraudScores = [], fraudScoreData = []) => {
  let riskLevel = {
    [RiskLevels.HIGH]: [],
    [RiskLevels.MEDIUM]: [],
    [RiskLevels.LOW]: [],
  };
  fraudScores.forEach(score => {
    const riskSetting = getRiskLevel(
      0,
      score.raw_score,
      score.name,
      score.attribute,
      fraudScoreData,
    );
    riskLevel[riskSetting].push({ name: score.name, ...score, riskLevel: riskSetting });
  });
  riskLevel = [RiskLevels.HIGH, RiskLevels.MEDIUM, RiskLevels.LOW].map(
    risk => riskLevel[risk].sort((a, b) => (a.name > b.name ? 1 : -1)),
  );
  return [].concat(...riskLevel);
};

const FraudPropTypes = {
  fraud: PropTypes.shape({
    score: PropTypes.number,
    sources: PropTypes.objectOf(PropTypes.object),
  }).isRequired,
  evaluationToken: PropTypes.string.isRequired,
};

function Fraud({ fraud, evaluationToken }) {
  const { feature_flags: featureFlags } = useFetchedUserContext();
  const [showHiddenSources, setShowHiddenSources] = useState(false);
  const [fraudData, setFraudData] = useState();
  useEffect(() => {
    ajaxFetch(routes.serviceFraudScores).then(({ scores }) => {
      setFraudData(scores);
    });
  }, []);
  const buttonText = showHiddenSources ? 'See Less' : 'See More';
  const { sources = {} } = fraud;
  const cardNumberThreshold = 4;

  const useEvaluationFraudScoresDBSourceOfTruth = featureFlags
    ?.['use-evaluation-fraud-scores-db-source-of-truth'] ?? false;
  const { data: fraudScores, isLoading } = useEvaluationFraudScores({ evaluationToken });
  const shouldShowLoadingSpinner = useEvaluationFraudScoresDBSourceOfTruth && isLoading;

  const sortedSources = useEvaluationFraudScoresDBSourceOfTruth
    ? sortFraudScoresByRisk(fraudScores, fraudData)
    : sortKeysByRisk(sources, fraudData);
  const displayedSourceKeys = sortedSources.slice(0, cardNumberThreshold);
  const hiddenSourceKeys = sortedSources.slice(cardNumberThreshold);
  const hasHiddenSourceKeys = !!hiddenSourceKeys.length;

  // Some of the title names are very long and must be shortened. This object maps those long names
  const getCards = sourceKeys => (
    sourceKeys.map(source => {
      if (typeof source.raw_score === 'object') {
        log(
          'ERROR',
          '', // not needed for captureExceptionsToSentry
          {
            name: source.name,
            attribute: source.attribute,
            risk_score: source.risk_score,
          },
          new Error('Invalid risk score format'),
        );
      }
      return (
        <Card
          key={source.name}
          fraudData={fraudData || []}
          title={source.name}
          displayName={source.display_name}
          model={source.name}
          subtitle={source.attribute}
          score={removeLeadingZero(source.raw_score)}
          riskLevel={source.riskLevel}
        />
      );
    })
  );

  const onClickButton = () => {
    setShowHiddenSources(!showHiddenSources);
  };

  return (
    <div>
      <p className="bold">Fraud</p>
      <div className={displayedSourceKeys.length ? 'cards-container' : null}>
        {shouldShowLoadingSpinner && <Spinner />}
        {displayedSourceKeys.length
          ? getCards(displayedSourceKeys)
          : <p className="light-grey small-text"><i>No fraud scores found</i></p>}
      </div>
      {hasHiddenSourceKeys && (
        <Collapse isOpen={showHiddenSources}>
          <div className="cards-container">
            {getCards(hiddenSourceKeys)}
          </div>
        </Collapse>
      )}
      {hasHiddenSourceKeys && (
        <div className="flex">
          <Button
            className="btn-link weight-normal card-button"
            onClick={onClickButton}
            variant={ButtonVariant.tertiary}
          >
            {buttonText}
          </Button>
        </div>
      )}
    </div>
  );
}
Fraud.propTypes = FraudPropTypes;

export default Fraud;
